// CB:BEGIN fr-001-admin-settings-tab
import React from 'react';
import { useCoreBidsSettings } from '../../hooks/useCoreBidsSettings';
import { SettingsSection } from '../settings/SettingsSection';
import { SettingsCheckbox } from '../settings/SettingsCheckbox';
import { SettingsSaveBar } from '../settings/SettingsSaveBar';

export default function CoreBidsAdminSettingsTab() {
  const s = useCoreBidsSettings();

  // Stop Clerk modal from closing on clicks (bubble phase only, on tab root)
  const stopModalBubble = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  if (s.loading) {
    return (
      <div onClick={stopModalBubble} style={{ padding: '24px', maxWidth: '600px' }}>
        <div style={{ padding: '20px', textAlign: 'center', color: '#64748b' }}>Loading...</div>
      </div>
    );
  }

  if (!s.data || !s.data.adminSettings) {
    return (
      <div onClick={stopModalBubble} style={{ padding: '24px', maxWidth: '600px' }}>
        <div style={{ padding: '20px', color: '#dc2626' }}>
          {s.error || 'Admin settings not available'}
        </div>
      </div>
    );
  }

  const settings = s.data.adminSettings;

  const handleSave = async () => {
    try {
      await s.save();
    } catch (err) {
      // Error is already set in hook
    }
  };

  return (
    <div onClick={stopModalBubble} className="corebids-settings" style={{ padding: '24px', maxWidth: '600px' }}>
      <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '24px', color: '#0f172a' }}>
        Admin Settings
      </h2>

      {s.error && !s.error.startsWith('✅') && (
        <div style={{ 
          padding: '12px 16px', 
          background: '#fef2f2', 
          color: '#dc2626', 
          borderRadius: '8px', 
          marginBottom: '20px',
          fontSize: '14px',
          border: '1px solid #fecaca'
        }}>
          {s.error}
        </div>
      )}

      <SettingsSection title="Escalation Settings">
        <label style={{ display: 'block', marginBottom: '12px' }}>
          <span style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px', color: '#334155' }}>Escalation SLA (minutes)</span>
          <input
            type="number"
            value={settings.escalationSlaMinutes}
            onChange={(e) => s.updateLocal({
              adminSettings: {
                ...settings,
                escalationSlaMinutes: parseInt(e.target.value) || 60,
              },
            })}
            min="1"
            style={{ 
              width: '100%', 
              padding: '8px 12px', 
              borderRadius: '6px', 
              border: '1px solid #cbd5e1',
              fontSize: '14px',
              color: '#1e293b',
              background: 'white'
            }}
          />
        </label>

        <label style={{ display: 'block', marginBottom: '12px' }}>
          <span style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px', color: '#334155' }}>Escalation Threshold Count</span>
          <input
            type="number"
            value={settings.escalationThresholdCount}
            onChange={(e) => s.updateLocal({
              adminSettings: {
                ...settings,
                escalationThresholdCount: parseInt(e.target.value) || 3,
              },
            })}
            min="1"
            style={{ 
              width: '100%', 
              padding: '8px 12px', 
              borderRadius: '6px', 
              border: '1px solid #cbd5e1',
              fontSize: '14px',
              color: '#1e293b',
              background: 'white'
            }}
          />
        </label>
      </SettingsSection>

      <SettingsSection title="On-Call Contact">
        <label style={{ display: 'block', marginBottom: '12px' }}>
          <span style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px', color: '#334155' }}>On-Call Email</span>
          <input
            type="email"
            value={settings.onCallEmail || ''}
            onChange={(e) => s.updateLocal({
              adminSettings: {
                ...settings,
                onCallEmail: e.target.value || null,
              },
            })}
            placeholder="oncall@example.com"
            style={{ 
              width: '100%', 
              padding: '8px 12px', 
              borderRadius: '6px', 
              border: '1px solid #cbd5e1',
              fontSize: '14px',
              color: '#1e293b',
              background: 'white'
            }}
          />
        </label>

        <label style={{ display: 'block', marginBottom: '12px' }}>
          <span style={{ display: 'block', marginBottom: '6px', fontWeight: '500', fontSize: '14px', color: '#334155' }}>On-Call SMS</span>
          <input
            type="tel"
            value={settings.onCallSms || ''}
            onChange={(e) => s.updateLocal({
              adminSettings: {
                ...settings,
                onCallSms: e.target.value || null,
              },
            })}
            placeholder="+1234567890"
            style={{ 
              width: '100%', 
              padding: '8px 12px', 
              borderRadius: '6px', 
              border: '1px solid #cbd5e1',
              fontSize: '14px',
              color: '#1e293b',
              background: 'white'
            }}
          />
        </label>
      </SettingsSection>

      <SettingsSection title="Security Settings">
        <div className="mb-5">
          <SettingsCheckbox
            id="notify-security"
            label="Notify on Security Alerts"
            description="Receive notifications when security events are detected."
            checked={settings.notifyOnSecurityAlerts}
            onCheckedChange={(checked) => s.updateLocal({
              adminSettings: {
                ...settings,
                notifyOnSecurityAlerts: checked,
              },
            })}
            testId="admin-security-alerts"
          />
        </div>
        <div className="mb-5">
          <SettingsCheckbox
            id="two-person-approval"
            label="Require Two-Person Approval for Overrides"
            description="Require a second admin to approve sensitive setting overrides."
            checked={settings.requireTwoPersonApprovalForOverrides}
            onCheckedChange={(checked) => s.updateLocal({
              adminSettings: {
                ...settings,
                requireTwoPersonApprovalForOverrides: checked,
              },
            })}
          />
        </div>
        <div className="mb-5">
          <SettingsCheckbox
            id="lock-sensitive-ips"
            label="Lock Sensitive Actions to Allowlisted IPs"
            description="Restrict sensitive admin actions to pre-approved IP addresses. (Note: IP allowlist must be configured separately)"
            checked={settings.lockSensitiveActionsToAllowlistedIps}
            onCheckedChange={(checked) => s.updateLocal({
              adminSettings: {
                ...settings,
                lockSensitiveActionsToAllowlistedIps: checked,
              },
            })}
          />
        </div>
      </SettingsSection>

      <SettingsSaveBar
        dirty={s.dirty}
        saving={s.saving}
        error={s.error}
        lastSavedAt={s.lastSavedAt}
        onSave={handleSave}
        onRevert={s.resetLocalToServer}
      />
    </div>
  );
}
// CB:END fr-001-admin-settings-tab
